/**
 * Smart Parts Kit — AI-powered parts prediction for maintenance techs.
 *
 * Pure functions that analyze work order history and parts usage to predict
 * what parts a tech should bring to a job BEFORE they leave the shop.
 * Reduces return trips (the #1 efficiency killer in maintenance).
 *
 * Data model expectations:
 * - Work orders have: { id, unitId, propertyId, description, category, status }
 * - Parts usage has: { woId, woNumber, partName, qty, cost, vendor }
 * - Labor entries have: { woId, description/notes }
 */

// ─── Issue Classification ────────────────────────────────────────────────────

/**
 * Keyword-based issue type detection from WO description.
 * Returns an array of detected issue types.
 */
const ISSUE_KEYWORDS = {
  plumbing:   ['leak', 'faucet', 'toilet', 'drain', 'pipe', 'water heater', 'disposal', 'clog', 'shower', 'tub', 'valve', 'supply line', 'p-trap', 'wax ring'],
  electrical: ['outlet', 'switch', 'light', 'breaker', 'gfci', 'wire', 'fixture', 'fan', 'electrical', 'circuit', 'dimmer', 'ballast'],
  hvac:       ['ac', 'a/c', 'heat', 'furnace', 'thermostat', 'hvac', 'filter', 'condenser', 'duct', 'blower', 'refrigerant', 'capacitor'],
  appliance:  ['dishwasher', 'refrigerator', 'fridge', 'oven', 'stove', 'range', 'microwave', 'washer', 'dryer', 'disposal', 'appliance'],
  door_lock:  ['door', 'lock', 'deadbolt', 'knob', 'handle', 'hinge', 'strike plate', 'closer', 'weatherstrip'],
  drywall:    ['drywall', 'hole', 'patch', 'wall', 'crack', 'texture', 'mud', 'tape'],
  flooring:   ['floor', 'carpet', 'tile', 'vinyl', 'lvp', 'laminate', 'grout', 'baseboard'],
  paint:      ['paint', 'touch up', 'touch-up', 'primer', 'stain'],
  pest:       ['pest', 'bug', 'roach', 'mouse', 'mice', 'rat', 'ant', 'insect'],
  turnover:   ['turn', 'turnover', 'make ready', 'move out', 'move-out', 'moveout', 'vacancy'],
};

export function classifyIssueType(description) {
  if (!description || typeof description !== 'string') return [];

  const desc = description.toLowerCase();
  const matched = [];

  for (const [issueType, keywords] of Object.entries(ISSUE_KEYWORDS)) {
    if (keywords.some(kw => {
      // Use word boundary matching for short keywords to avoid false positives
      // (e.g., "ant" matching "constantly")
      if (kw.length <= 3) {
        const regex = new RegExp(`\\b${kw}\\b`, 'i');
        return regex.test(desc);
      }
      return desc.includes(kw);
    })) {
      matched.push(issueType);
    }
  }

  return matched.length > 0 ? matched : ['general'];
}

// ─── Parts History Analysis ──────────────────────────────────────────────────

/**
 * Analyze historical parts usage for a unit to find commonly needed parts.
 * Returns parts ranked by frequency.
 */
export function analyzePartsHistory(partsUsage, workOrders, unitId) {
  if (!Array.isArray(partsUsage) || !Array.isArray(workOrders) || !unitId) return [];

  // Get all WO IDs for this unit
  const unitWOIds = new Set(
    workOrders.filter(wo => wo.unitId === unitId).map(wo => wo.id || wo.woNumber)
  );

  // Find all parts used on those WOs
  const unitParts = partsUsage.filter(p =>
    unitWOIds.has(p.woId) || unitWOIds.has(p.woNumber)
  );

  // Aggregate by part name
  const partMap = {};
  for (const p of unitParts) {
    const name = (p.partName || p.description || '').trim();
    if (!name) continue;

    if (!partMap[name]) {
      partMap[name] = { partName: name, totalQty: 0, totalCost: 0, useCount: 0, lastUsed: null };
    }
    partMap[name].totalQty += (p.qty || 1);
    partMap[name].totalCost += (p.cost || 0);
    partMap[name].useCount += 1;

    // Track most recent usage
    if (p.date && (!partMap[name].lastUsed || p.date > partMap[name].lastUsed)) {
      partMap[name].lastUsed = p.date;
    }
  }

  return Object.values(partMap).sort((a, b) => b.useCount - a.useCount);
}

/**
 * Analyze parts usage patterns across similar issue types property-wide.
 * Finds what parts are commonly used for a given issue type.
 */
export function getFrequentPartsForIssueType(partsUsage, workOrders, issueType, propertyId = null) {
  if (!Array.isArray(partsUsage) || !Array.isArray(workOrders) || !issueType) return [];

  // Find WOs matching the issue type
  const matchingWOs = workOrders.filter(wo => {
    if (propertyId && wo.propertyId !== propertyId) return false;
    const types = classifyIssueType(wo.description);
    return types.includes(issueType);
  });

  const woIds = new Set(matchingWOs.map(wo => wo.id || wo.woNumber));

  // Aggregate parts from matching WOs
  const partMap = {};
  for (const p of partsUsage) {
    if (!woIds.has(p.woId) && !woIds.has(p.woNumber)) continue;

    const name = (p.partName || p.description || '').trim();
    if (!name) continue;

    if (!partMap[name]) {
      partMap[name] = { partName: name, useCount: 0, avgCost: 0, totalCost: 0 };
    }
    partMap[name].useCount += 1;
    partMap[name].totalCost += (p.cost || 0);
  }

  // Calculate averages and sort
  return Object.values(partMap)
    .map(p => ({ ...p, avgCost: p.useCount > 0 ? p.totalCost / p.useCount : 0 }))
    .sort((a, b) => b.useCount - a.useCount);
}

// ─── Parts Prediction ────────────────────────────────────────────────────────

/**
 * Predict parts needed for a work order based on:
 * 1. Unit-specific parts history
 * 2. Property-wide patterns for similar issues
 * 3. Issue type common parts database
 *
 * Returns ranked list of predicted parts with confidence.
 */
export function predictPartsForWO(partsUsage, workOrders, wo) {
  if (!wo || !Array.isArray(partsUsage) || !Array.isArray(workOrders)) return [];

  const issueTypes = classifyIssueType(wo.description);
  const predictions = new Map(); // partName -> { sources, confidence }

  // Source 1: Unit history (highest signal)
  const unitParts = analyzePartsHistory(partsUsage, workOrders, wo.unitId);
  for (const part of unitParts) {
    predictions.set(part.partName, {
      partName: part.partName,
      confidence: Math.min(0.9, 0.3 + (part.useCount * 0.2)),
      sources: ['unit_history'],
      avgCost: part.totalCost / part.useCount,
    });
  }

  // Source 2: Property-wide issue-type patterns
  for (const issueType of issueTypes) {
    const issueParts = getFrequentPartsForIssueType(partsUsage, workOrders, issueType, wo.propertyId);
    for (const part of issueParts.slice(0, 10)) {
      const existing = predictions.get(part.partName);
      if (existing) {
        // Boost confidence if multiple sources agree
        existing.confidence = Math.min(0.95, existing.confidence + 0.15);
        if (!existing.sources.includes('property_pattern')) {
          existing.sources.push('property_pattern');
        }
      } else {
        predictions.set(part.partName, {
          partName: part.partName,
          confidence: Math.min(0.7, 0.1 + (part.useCount * 0.1)),
          sources: ['property_pattern'],
          avgCost: part.avgCost,
        });
      }
    }
  }

  return Array.from(predictions.values())
    .sort((a, b) => b.confidence - a.confidence);
}

/**
 * Calculate estimated budget for predicted parts.
 */
export function calculatePartsBudget(predictions) {
  if (!Array.isArray(predictions)) return { total: 0, highConfidence: 0, allParts: 0 };

  const highConf = predictions.filter(p => p.confidence >= 0.5);
  const highConfidenceTotal = highConf.reduce((sum, p) => sum + (p.avgCost || 0), 0);
  const allTotal = predictions.reduce((sum, p) => sum + (p.avgCost || 0), 0);

  return {
    total: Math.round(allTotal * 100) / 100,
    highConfidence: Math.round(highConfidenceTotal * 100) / 100,
    allParts: Math.round(allTotal * 100) / 100,
    itemCount: predictions.length,
    highConfidenceCount: highConf.length,
  };
}

/**
 * Format parts predictions as text for display or AI prompts.
 */
export function formatPartsListForPrompt(predictions) {
  if (!Array.isArray(predictions) || predictions.length === 0) return 'No parts data available.';

  return predictions.map(p => {
    const conf = Math.round(p.confidence * 100);
    const cost = p.avgCost ? ` (~$${p.avgCost.toFixed(2)})` : '';
    return `- ${p.partName}${cost} [${conf}% likely]`;
  }).join('\n');
}
